/**
 * Hero's Journey Share Page
 * Canvas-based shareable cards with design options.
 * - Always available after completing any day
 * - Multiple design themes
 * - Embedded affiliate/referral link in QR code
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Share2,
  Download,
  Trophy,
  Flame,
  Compass,
  Star,
  Sparkles,
  Palette,
  Lock,
  Check,
  Gift
} from "lucide-react";
import axios from "axios";
import QRCode from "qrcode";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Design themes with gradient colors
const DESIGN_THEMES = {
  cosmic_purple: {
    name: "Cosmic Purple",
    colors: ["#1e1b4b", "#7c3aed", "#c026d3"],
    accent: "#f59e0b",
    textColor: "#ffffff"
  },
  golden_dawn: {
    name: "Golden Dawn",
    colors: ["#451a03", "#ea580c", "#facc15"],
    accent: "#ffffff",
    textColor: "#ffffff"
  },
  ocean_deep: {
    name: "Ocean Deep",
    colors: ["#0c4a6e", "#0891b2", "#22d3ee"],
    accent: "#fbbf24",
    textColor: "#ffffff"
  },
  forest_calm: {
    name: "Forest Calm",
    colors: ["#14532d", "#16a34a", "#86efac"],
    accent: "#fbbf24",
    textColor: "#ffffff"
  },
  midnight_rose: {
    name: "Midnight Rose",
    colors: ["#4c0519", "#e11d48", "#fda4af"],
    accent: "#ffffff",
    textColor: "#ffffff"
  },
  aurora_borealis: {
    name: "Aurora Borealis",
    colors: ["#042f2e", "#0d9488", "#5eead4"],
    accent: "#f59e0b",
    textColor: "#ffffff"
  },
  sunset_blaze: {
    name: "Sunset Blaze",
    colors: ["#7c2d12", "#f97316", "#fcd34d"],
    accent: "#ffffff",
    textColor: "#ffffff"
  },
  royal_velvet: {
    name: "Royal Velvet",
    colors: ["#3b0764", "#9333ea", "#e879f9"],
    accent: "#fbbf24",
    textColor: "#ffffff"
  },
  partner_gold: {
    name: "Partner Gold",
    colors: ["#1c1917", "#ca8a04", "#fef08a"],
    accent: "#ffffff",
    textColor: "#ffffff"
  },
  partner_platinum: {
    name: "Partner Platinum",
    colors: ["#18181b", "#71717a", "#f4f4f5"],
    accent: "#fbbf24",
    textColor: "#ffffff"
  },
  summit_beacon: {
    name: "Summit Beacon",
    colors: ["#0a0a1a", "#12122a", "#1a1a3e"],
    accent: "#f59e0b",
    textColor: "#ffffff",
    isMountain: true
  }
};

const HeroSharePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [progress, setProgress] = useState(null);
  const [journey, setJourney] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState("cosmic_purple");
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const canvasRef = useRef(null);
  const getToken = () => localStorage.getItem("reclaim_token");

  // Get milestone from URL if provided
  const milestoneDay = searchParams.get("day") ? parseInt(searchParams.get("day")) : null;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Auto-select milestone if day is provided in URL
    if (milestoneDay && progress) {
      const milestone = getMilestoneForDay(milestoneDay);
      if (milestone) {
        setSelectedMilestone(milestone);
      }
    } else if (progress && !selectedMilestone) {
      // Default to latest completed day
      const completedDays = progress.completed_days || [];
      if (completedDays.length > 0) {
        const latestDay = Math.max(...completedDays);
        setSelectedMilestone({
          id: `day_${latestDay}`,
          day: latestDay,
          type: "day",
          title: `Day ${((latestDay - 1) % 21) + 1}`,
          subtitle: getDayTitle(latestDay),
          level: Math.ceil(latestDay / 21)
        });
      }
    }
  }, [milestoneDay, progress]);

  const loadData = async () => {
    try {
      const [journeyRes, referralRes, designsRes] = await Promise.all([
        axios.get(`${API_URL}/api/hero-journey`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/referral/code`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/referral/share-designs`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      
      setProgress(journeyRes.data.progress);
      setJourney(journeyRes.data.journey);
      setReferralData(referralRes.data);
      setDesigns(designsRes.data.designs || []);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  const getDayTitle = (day) => {
    const dayContent = journey?.days?.find(d => d.day === day);
    return dayContent?.title || "Hero's Journey";
  };

  const getMilestoneForDay = (day) => {
    const level = Math.ceil(day / 21);
    const displayDay = ((day - 1) % 21) + 1;
    
    // Check for special milestones
    if (day === 21) {
      return { id: "level_1", day: 21, type: "level", title: "Level 1 Complete", subtitle: "The Hero Awakens", level: 1 };
    }
    if (day === 42) {
      return { id: "level_2", day: 42, type: "level", title: "Level 2 Complete", subtitle: "The Hero Steps Into Power", level: 2 };
    }
    if (day === 63) {
      return { id: "level_3", day: 63, type: "level", title: "Level 3 Complete", subtitle: "The Hero Returns", level: 3 };
    }
    if (day % 7 === 0) {
      const week = day / 7;
      return { id: `week_${week}`, day, type: "week", title: `Week ${week} Complete`, subtitle: "Steady Progress", level };
    }
    
    return {
      id: `day_${day}`,
      day,
      type: "day",
      title: `Day ${displayDay}`,
      subtitle: getDayTitle(day),
      level
    };
  };

  // Get all shareable milestones
  const getShareableMilestones = () => {
    if (!progress) return [];
    
    const completedDays = progress.completed_days || [];
    const milestones = [];
    
    completedDays.forEach(day => {
      const milestone = getMilestoneForDay(day);
      milestones.push(milestone);
    });
    
    // Sort by day descending (most recent first)
    return milestones.sort((a, b) => b.day - a.day);
  };

  // Generate canvas card
  const generateCard = useCallback(async () => {
    if (!canvasRef.current || !selectedMilestone) return;
    
    setIsGenerating(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Card dimensions (Instagram story ratio)
    canvas.width = 1080;
    canvas.height = 1920;
    
    const theme = DESIGN_THEMES[selectedDesign] || DESIGN_THEMES.cosmic_purple;
    
    // Background gradient
    const bgGrad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    bgGrad.addColorStop(0, theme.colors[0]);
    bgGrad.addColorStop(0.5, theme.colors[1]);
    bgGrad.addColorStop(1, theme.colors[2]);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add decorative stars
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height * 0.65;
      const size = Math.random() * 2.5 + 0.5;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Mountain theme special rendering
    if (theme.isMountain) {
      const daysCompleted = progress?.completed_days?.length || 0;
      const progressPercent = (daysCompleted / 63) * 100;
      const currentDay = progress?.current_day || 1;
      const currentLevel = progress?.current_level || 1;
      
      // Draw mountain silhouette
      const mountainGrad = ctx.createLinearGradient(0, canvas.height * 0.2, 0, canvas.height * 0.7);
      mountainGrad.addColorStop(0, "rgba(245, 158, 11, 0.4)");
      mountainGrad.addColorStop(0.4, "rgba(139, 92, 246, 0.3)");
      mountainGrad.addColorStop(1, "rgba(99, 102, 241, 0.2)");
      
      ctx.fillStyle = mountainGrad;
      ctx.beginPath();
      ctx.moveTo(0, canvas.height * 0.7);
      ctx.lineTo(canvas.width * 0.2, canvas.height * 0.7);
      ctx.lineTo(canvas.width * 0.35, canvas.height * 0.45);
      ctx.lineTo(canvas.width * 0.5, canvas.height * 0.2);
      ctx.lineTo(canvas.width * 0.65, canvas.height * 0.45);
      ctx.lineTo(canvas.width * 0.8, canvas.height * 0.7);
      ctx.lineTo(canvas.width, canvas.height * 0.7);
      ctx.lineTo(canvas.width, canvas.height * 0.7);
      ctx.closePath();
      ctx.fill();
      
      // Mountain outline
      ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Climbing path - background
      ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
      ctx.lineWidth = 8;
      ctx.setLineDash([15, 20]);
      ctx.beginPath();
      ctx.moveTo(canvas.width * 0.28, canvas.height * 0.62);
      ctx.quadraticCurveTo(canvas.width * 0.35, canvas.height * 0.52, canvas.width * 0.42, canvas.height * 0.42);
      ctx.quadraticCurveTo(canvas.width * 0.48, canvas.height * 0.32, canvas.width * 0.5, canvas.height * 0.22);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Climbing path - progress
      const pathGrad = ctx.createLinearGradient(canvas.width * 0.28, canvas.height * 0.62, canvas.width * 0.5, canvas.height * 0.22);
      pathGrad.addColorStop(0, "#10b981");
      pathGrad.addColorStop(1, "#fbbf24");
      ctx.strokeStyle = pathGrad;
      ctx.lineWidth = 10;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(canvas.width * 0.28, canvas.height * 0.62);
      
      // Draw path up to progress point
      const pathLength = progressPercent / 100;
      if (pathLength > 0) {
        const cp1x = canvas.width * 0.35;
        const cp1y = canvas.height * 0.52;
        const p1x = canvas.width * 0.28 + (canvas.width * 0.14) * Math.min(pathLength * 2, 1);
        const p1y = canvas.height * 0.62 - (canvas.height * 0.2) * Math.min(pathLength * 2, 1);
        ctx.quadraticCurveTo(cp1x, cp1y, p1x, p1y);
        
        if (pathLength > 0.5) {
          const remaining = (pathLength - 0.5) * 2;
          const p2x = canvas.width * 0.42 + (canvas.width * 0.08) * remaining;
          const p2y = canvas.height * 0.42 - (canvas.height * 0.2) * remaining;
          ctx.quadraticCurveTo(canvas.width * 0.48, canvas.height * 0.32, p2x, p2y);
        }
      }
      ctx.stroke();
      
      // Summit beacon glow
      const beaconIntensity = 0.3 + (progressPercent / 100) * 0.7;
      const beaconGrad = ctx.createRadialGradient(
        canvas.width * 0.5, canvas.height * 0.2, 0,
        canvas.width * 0.5, canvas.height * 0.2, 120
      );
      beaconGrad.addColorStop(0, `rgba(251, 191, 36, ${beaconIntensity})`);
      beaconGrad.addColorStop(0.5, `rgba(251, 191, 36, ${beaconIntensity * 0.4})`);
      beaconGrad.addColorStop(1, "transparent");
      ctx.fillStyle = beaconGrad;
      ctx.beginPath();
      ctx.arc(canvas.width * 0.5, canvas.height * 0.2, 120, 0, Math.PI * 2);
      ctx.fill();
      
      // Summit beacon core
      ctx.fillStyle = `rgba(251, 191, 36, ${beaconIntensity})`;
      ctx.beginPath();
      ctx.arc(canvas.width * 0.5, canvas.height * 0.2, 30, 0, Math.PI * 2);
      ctx.fill();
      
      // User position marker (if not complete)
      if (progressPercent < 100) {
        const markerX = canvas.width * 0.28 + (canvas.width * 0.22) * (progressPercent / 100);
        const markerY = canvas.height * 0.62 - (canvas.height * 0.4) * (progressPercent / 100);
        
        // Marker glow
        const markerGrad = ctx.createRadialGradient(markerX, markerY, 0, markerX, markerY, 40);
        markerGrad.addColorStop(0, "rgba(245, 158, 11, 0.6)");
        markerGrad.addColorStop(1, "transparent");
        ctx.fillStyle = markerGrad;
        ctx.beginPath();
        ctx.arc(markerX, markerY, 40, 0, Math.PI * 2);
        ctx.fill();
        
        // Marker core
        ctx.fillStyle = "#f59e0b";
        ctx.beginPath();
        ctx.arc(markerX, markerY, 18, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Day text - large
      ctx.fillStyle = theme.textColor;
      ctx.font = "bold 140px system-ui";
      ctx.textAlign = "center";
      ctx.fillText(`Day ${currentDay}`, canvas.width / 2, canvas.height * 0.78);
      
      // "of 63" text
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.font = "60px system-ui";
      ctx.fillText("of 63", canvas.width / 2, canvas.height * 0.83);
      
      // Level badge
      const levelNames = { 1: "Awakening", 2: "Transformation", 3: "Illumination" };
      ctx.fillStyle = theme.accent;
      roundRect(ctx, canvas.width / 2 - 120, canvas.height * 0.855, 240, 60, 30);
      ctx.fill();
      ctx.fillStyle = theme.colors[0];
      ctx.font = "bold 28px system-ui";
      ctx.fillText(levelNames[currentLevel] || "Awakening", canvas.width / 2, canvas.height * 0.855 + 40);
      
      // Stats row
      const statsY = canvas.height * 0.92;
      ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
      roundRect(ctx, 100, statsY, canvas.width - 200, 100, 20);
      ctx.fill();
      
      ctx.fillStyle = "#10b981";
      ctx.font = "bold 48px system-ui";
      ctx.fillText(daysCompleted.toString(), canvas.width * 0.25, statsY + 55);
      ctx.fillStyle = "#8b5cf6";
      ctx.fillText(`L${currentLevel}`, canvas.width * 0.5, statsY + 55);
      ctx.fillStyle = "#fbbf24";
      ctx.fillText(`${Math.round(progressPercent)}%`, canvas.width * 0.75, statsY + 55);
      
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.font = "22px system-ui";
      ctx.fillText("Days", canvas.width * 0.25, statsY + 85);
      ctx.fillText("Level", canvas.width * 0.5, statsY + 85);
      ctx.fillText("Complete", canvas.width * 0.75, statsY + 85);
      
      // QR Code with referral link for mountain theme
      try {
        const qrUrl = referralData?.link || `${window.location.origin}/hero-journey`;
        const qrDataUrl = await QRCode.toDataURL(qrUrl, {
          width: 160,
          margin: 0,
          color: { dark: "#ffffff", light: "#00000000" }
        });
        const qrImg = new Image();
        qrImg.onload = () => {
          // Draw QR code
          ctx.drawImage(qrImg, canvas.width / 2 - 80, canvas.height * 0.94, 160, 160);
          
          // Add referral code below QR
          if (referralData?.code) {
            ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
            ctx.font = "20px system-ui";
            ctx.fillText(`Join with code: ${referralData.code}`, canvas.width / 2, canvas.height * 0.99);
          }
          
          setPreviewUrl(canvas.toDataURL("image/png"));
          setIsGenerating(false);
        };
        qrImg.onerror = () => {
          // Fallback without QR
          ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
          ctx.font = "24px system-ui";
          ctx.fillText("Reclaim · Hero's Journey", canvas.width / 2, canvas.height * 0.98);
          setPreviewUrl(canvas.toDataURL("image/png"));
          setIsGenerating(false);
        };
        qrImg.src = qrDataUrl;
      } catch (err) {
        // Fallback without QR
        ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
        ctx.font = "24px system-ui";
        ctx.fillText("Reclaim · Hero's Journey", canvas.width / 2, canvas.height * 0.98);
        setPreviewUrl(canvas.toDataURL("image/png"));
        setIsGenerating(false);
      }
      return;
    }
    
    // Central glowing orb
    const orbGrad = ctx.createRadialGradient(
      canvas.width / 2, canvas.height * 0.32, 0,
      canvas.width / 2, canvas.height * 0.32, 180
    );
    orbGrad.addColorStop(0, theme.accent);
    orbGrad.addColorStop(0.4, `${theme.accent}60`);
    orbGrad.addColorStop(1, "transparent");
    ctx.fillStyle = orbGrad;
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height * 0.32, 180, 0, Math.PI * 2);
    ctx.fill();
    
    // Icon
    ctx.fillStyle = theme.textColor;
    ctx.font = "bold 100px system-ui";
    ctx.textAlign = "center";
    const iconMap = { 
      level: "🏆", 
      week: "⭐", 
      day: "✨",
      journey: "🧭"
    };
    ctx.fillText(iconMap[selectedMilestone.type] || "✨", canvas.width / 2, canvas.height * 0.35);
    
    // Main title
    ctx.fillStyle = theme.textColor;
    ctx.font = "bold 64px system-ui";
    ctx.fillText(selectedMilestone.title, canvas.width / 2, canvas.height * 0.48);
    
    // Subtitle (day title)
    ctx.fillStyle = `${theme.textColor}cc`;
    ctx.font = "36px system-ui";
    const subtitle = selectedMilestone.subtitle.length > 35 
      ? selectedMilestone.subtitle.substring(0, 32) + "..."
      : selectedMilestone.subtitle;
    ctx.fillText(subtitle, canvas.width / 2, canvas.height * 0.54);
    
    // Level badge
    ctx.fillStyle = theme.accent;
    roundRect(ctx, canvas.width / 2 - 70, canvas.height * 0.58, 140, 44, 22);
    ctx.fill();
    ctx.fillStyle = theme.colors[0];
    ctx.font = "bold 22px system-ui";
    ctx.fillText(`LEVEL ${selectedMilestone.level}`, canvas.width / 2, canvas.height * 0.58 + 30);
    
    // Stats section
    const statsY = canvas.height * 0.68;
    ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
    roundRect(ctx, 80, statsY, canvas.width - 160, 160, 24);
    ctx.fill();
    
    const daysCompleted = progress?.completed_days?.length || 0;
    const streak = progress?.streak || 0;
    const level = progress?.current_level || 1;
    
    ctx.fillStyle = theme.textColor;
    ctx.font = "bold 44px system-ui";
    ctx.fillText(daysCompleted.toString(), canvas.width * 0.25, statsY + 65);
    ctx.fillText(streak.toString(), canvas.width * 0.5, statsY + 65);
    ctx.fillText(level.toString(), canvas.width * 0.75, statsY + 65);
    
    ctx.fillStyle = `${theme.textColor}99`;
    ctx.font = "22px system-ui";
    ctx.fillText("Days", canvas.width * 0.25, statsY + 100);
    ctx.fillText("Streak", canvas.width * 0.5, statsY + 100);
    ctx.fillText("Level", canvas.width * 0.75, statsY + 100);
    
    // Progress bar
    const progressY = statsY + 130;
    ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
    roundRect(ctx, 120, progressY, canvas.width - 240, 16, 8);
    ctx.fill();
    
    const progressWidth = (daysCompleted / 63) * (canvas.width - 240);
    ctx.fillStyle = theme.accent;
    roundRect(ctx, 120, progressY, progressWidth, 16, 8);
    ctx.fill();
    
    // Referral message
    ctx.fillStyle = `${theme.textColor}cc`;
    ctx.font = "26px system-ui";
    ctx.fillText("Join me on the Hero's Journey", canvas.width / 2, canvas.height * 0.84);
    
    // Brand footer
    ctx.fillStyle = `${theme.textColor}80`;
    ctx.font = "24px system-ui";
    ctx.fillText("Reclaim App", canvas.width / 2, canvas.height * 0.88);
    
    // QR Code with referral link
    try {
      const qrUrl = referralData?.link || `${window.location.origin}/hero-journey`;
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 140,
        margin: 0,
        color: { dark: theme.textColor, light: "#00000000" }
      });
      const qrImg = new Image();
      qrImg.onload = () => {
        ctx.drawImage(qrImg, canvas.width / 2 - 70, canvas.height * 0.90, 140, 140);
        
        // Add referral code below QR
        if (referralData?.code) {
          ctx.fillStyle = `${theme.textColor}99`;
          ctx.font = "18px system-ui";
          ctx.fillText(`Code: ${referralData.code}`, canvas.width / 2, canvas.height * 0.98);
        }
        
        setPreviewUrl(canvas.toDataURL("image/png"));
        setIsGenerating(false);
      };
      qrImg.src = qrDataUrl;
    } catch (err) {
      setPreviewUrl(canvas.toDataURL("image/png"));
      setIsGenerating(false);
    }
  }, [progress, selectedMilestone, selectedDesign, referralData]);

  // Rounded rectangle helper
  const roundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  // Generate when milestone or design changes
  useEffect(() => {
    if (selectedMilestone) {
      generateCard();
    }
  }, [selectedMilestone, selectedDesign, generateCard]);

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement("a");
    link.download = `hero-journey-day-${selectedMilestone?.day || "share"}.png`;
    link.href = previewUrl;
    link.click();
    toast.success("Image downloaded!");
  };

  const handleShare = async () => {
    if (!previewUrl) return;
    
    try {
      const blob = await fetch(previewUrl).then(r => r.blob());
      const file = new File([blob], "hero-journey-share.png", { type: "image/png" });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "My Hero's Journey Progress",
          text: `${selectedMilestone?.title} - ${selectedMilestone?.subtitle}\n\nJoin me: ${referralData?.link || ''}`,
          files: [file]
        });
      } else {
        handleDownload();
      }
    } catch (err) {
      handleDownload();
    }
  };

  const milestones = getShareableMilestones();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#12122a] to-[#0a0a1a]">
      {/* Hidden canvas for generation */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/hero-journey")}
          className="text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-white font-semibold">Share Your Progress</h1>
        <button
          onClick={() => setShowDesignPicker(true)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="design-picker-btn"
        >
          <Palette size={18} className="text-amber-400" />
        </button>
      </div>

      <div className="px-6 pb-8">
        {/* Referral info banner */}
        {referralData && (
          <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-3">
            <Gift size={18} className="text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white/80 text-sm">Your referral code: <span className="font-bold text-amber-400">{referralData.code}</span></p>
              <p className="text-white/50 text-xs">Earn credits when friends join!</p>
            </div>
          </div>
        )}

        {/* No milestones yet */}
        {milestones.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Compass size={40} className="text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Complete Your First Day
            </h2>
            <p className="text-white/60 mb-6">
              Share your progress after completing Day 1!
            </p>
            <button
              onClick={() => navigate("/hero-journey")}
              className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold"
            >
              Continue Journey
            </button>
          </div>
        )}

        {/* Milestone selector */}
        {milestones.length > 0 && (
          <>
            <div className="mb-4">
              <p className="text-white/60 text-sm mb-2">Select moment to share</p>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-6 px-6">
                {milestones.slice(0, 10).map((milestone) => (
                  <button
                    key={milestone.id}
                    onClick={() => setSelectedMilestone(milestone)}
                    className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedMilestone?.id === milestone.id
                        ? 'bg-amber-500 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {milestone.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="relative aspect-[9/16] bg-black/30 rounded-2xl overflow-hidden flex items-center justify-center mb-4">
              {isGenerating ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-white/60 text-sm">Creating card...</p>
                </div>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Share card preview"
                  className="w-full h-full object-contain"
                />
              ) : null}
            </div>

            {/* Action buttons */}
            {previewUrl && (
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDownload}
                  className="flex-1 py-4 rounded-xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="download-btn"
                >
                  <Download size={20} />
                  Download
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleShare}
                  className="flex-1 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center justify-center gap-2"
                  data-testid="share-btn"
                >
                  <Share2 size={20} />
                  Share
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Design Picker Modal */}
      <AnimatePresence>
        {showDesignPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center"
          >
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowDesignPicker(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="relative w-full max-w-lg bg-gradient-to-b from-[#1a1a2e] to-[#12122a] rounded-t-3xl p-6 pb-10"
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <h3 className="text-white font-semibold text-lg mb-4">Choose Design</h3>
              
              <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
                {designs.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => {
                      if (!design.locked) {
                        setSelectedDesign(design.id);
                        setShowDesignPicker(false);
                      }
                    }}
                    disabled={design.locked}
                    className={`relative p-3 rounded-xl border-2 transition-all ${
                      selectedDesign === design.id
                        ? 'border-amber-500'
                        : design.locked
                          ? 'border-white/10 opacity-50'
                          : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`design-${design.id}`}
                  >
                    {/* Color preview */}
                    <div 
                      className="h-20 rounded-lg mb-2"
                      style={{
                        background: `linear-gradient(135deg, ${design.preview_colors.join(', ')})`
                      }}
                    />
                    <p className="text-white text-sm font-medium">{design.name}</p>
                    {design.category === "premium" && (
                      <span className="text-amber-400 text-xs">Premium</span>
                    )}
                    {design.category === "partner" && (
                      <span className="text-emerald-400 text-xs">Partner</span>
                    )}
                    
                    {/* Lock icon */}
                    {design.locked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                        <Lock size={24} className="text-white/60" />
                      </div>
                    )}
                    
                    {/* Selected check */}
                    {selectedDesign === design.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Unlock more designs CTA */}
              <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/70 text-sm text-center">
                  Unlock premium designs with credits or become a partner!
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeroSharePage;
